import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { clinicianService } from '../services/clinicianService';

const ClinicianEditAvailabilityPage = () => {
  const navigate = useNavigate();
  
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Edit Availability';
  }, []);

  // State for data
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [unavailabilityReasons, setUnavailabilityReasons] = useState({
    sickLeave: false,
    annualLeave: false,
    other: false
  });
  const [otherReason, setOtherReason] = useState('');

  // New state for edit availability
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [presetTimes, setPresetTimes] = useState({
    '6:00am - 2:00pm': false,
    '8:00am - 4:00pm': false,
    '10:00am - 6:00pm': false,
    '7:00am - 3:00pm': false,
    '9:00am - 5:00pm': false
  });
  const [applyToAllDays, setApplyToAllDays] = useState(false);
  const [customStartTime, setCustomStartTime] = useState('');
  const [customFinishTime, setCustomFinishTime] = useState('');
  const [applyCustomToAllDays, setApplyCustomToAllDays] = useState(false);
  const [clinicianId, setClinicianId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Handle submit for both preset and custom times
  const handleSubmit = async () => {
    if (!clinicianId) {
      toast.error('Clinician not found');
      return;
    }

    if (selectedDays.length === 0) {
      toast.error('Please select at least one date');
      return;
    }

    try {
      setLoading(true);
      
      // Determine which times to use
      let startTime = '09:00';
      let endTime = '17:00';
      
      // Check if any preset time is selected
      const selectedPreset = Object.entries(presetTimes).find(([_, selected]) => selected);
      if (selectedPreset) {
        const timeRange = selectedPreset[0];
        // Parse preset time ranges like "6:00am - 2:00pm"
        const [start, end] = timeRange.split(' - ');
        startTime = convertTo24Hour(start);
        endTime = convertTo24Hour(end);
      } else if (customStartTime && customFinishTime) {
        // Use custom times if provided
        startTime = customStartTime;
        endTime = customFinishTime;
      }

      // Convert selected days to ISO date strings (avoiding timezone issues)
      const dates = selectedDays.map(dayKey => {
        const [year, month, day] = dayKey.split('-');
        // Note: month is already 0-indexed from getMonth(), so use it directly
        const date = new Date(parseInt(year), parseInt(month), parseInt(day));
        // Use local date formatting to avoid timezone conversion issues
        const localYear = date.getFullYear();
        const localMonth = String(date.getMonth() + 1).padStart(2, '0');
        const localDay = String(date.getDate()).padStart(2, '0');
        return `${localYear}-${localMonth}-${localDay}`;
      });

      // Apply availability with the selected times
      await clinicianService.bulkSetAvailability(clinicianId, {
        dates,
        is_available: true,
        preset_from: startTime,
        preset_to: endTime,
        slot_minutes: 15
      });

      toast.success(`Availability updated for ${dates.length} day(s) with times ${startTime} - ${endTime}`);
      
      // Reset form
      setSelectedDays([]);
      setPresetTimes({
        '6:00am - 2:00pm': false,
        '8:00am - 4:00pm': false,
        '10:00am - 6:00pm': false,
        '7:00am - 3:00pm': false,
        '9:00am - 5:00pm': false
      });
      setCustomStartTime('');
      setCustomFinishTime('');
      setApplyToAllDays(false);
      setApplyCustomToAllDays(false);

    } catch (error) {
      toast.error(error.message || 'Failed to update availability');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert 12-hour time to 24-hour format
  const convertTo24Hour = (time12h) => {
    const [time, modifier] = time12h.split(/([ap]m)/i);
    let [hours, minutes] = time.split(':');
    if (hours === '12') {
      hours = modifier.toLowerCase() === 'am' ? '00' : '12';
    } else if (modifier.toLowerCase() === 'pm') {
      hours = parseInt(hours, 10) + 12;
    }
    return `${String(hours).padStart(2, '0')}:${minutes || '00'}`;
  };

  useEffect(() => {
    const init = async () => {
      try {
        const user = clinicianService.getCurrentUser();
        if (!user) throw new Error('User not authenticated');
        const clinician = await clinicianService.getClinicianByUserId(user.id);
        setClinicianId(clinician.id);
      } catch (e) {
        toast.error(e.message || 'Failed to load clinician');
      }
    };
    init();
  }, []);

  const toIsoDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Generate real calendar days for selected year and month (including weekends)
  const generateCalendarDays = () => {
    const year = selectedYear;
    const month = selectedMonth;
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Find the first Sunday of the calendar grid (to show complete weeks)
    const startDate = new Date(firstDay);
    const firstDayOfWeek = firstDay.getDay();
    
    // Go back to the previous Sunday (0)
    if (firstDayOfWeek !== 0) {
      startDate.setDate(startDate.getDate() - firstDayOfWeek);
    }
    
    const days = [];
    const currentDate = new Date(startDate);
    
    // Generate 42 days (6 weeks * 7 days) to ensure we show complete weeks
    while (days.length < 42) {
      days.push({
        date: new Date(currentDate),
        isCurrentMonth: currentDate.getMonth() === month,
        day: currentDate.getDate()
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  // For edit page, no pre-determined available dates - user selects their own availability
  const getAvailableDates = () => {
    // Return empty array - no pre-determined available dates in edit mode
    return [];
  };

  const calendarDays = generateCalendarDays();
  const availableDates = getAvailableDates();

  const handleYearChange = (direction) => {
    setSelectedYear(prev => direction === 'next' ? prev + 1 : prev - 1);
  };

  const handleMonthChange = (direction) => {
    setSelectedMonth(prev => {
      if (direction === 'next') {
        return prev === 11 ? 0 : prev + 1;
      } else {
        return prev === 0 ? 11 : prev - 1;
      }
    });
  };

  const handleMonthSelection = (monthIndex, shiftKey = false) => {
    if (shiftKey) {
      // Multi-selection with shift key
      setSelectedMonths(prev => {
        if (prev.includes(monthIndex)) {
          return prev.filter(m => m !== monthIndex);
        } else {
          return [...prev, monthIndex];
        }
      });
    } else {
      // Single selection - change the calendar view to show this month
      setSelectedMonth(monthIndex);
      // Also update selected months for tracking
      setSelectedMonths([monthIndex]);
    }
  };

  const handleDaySelection = (day, shiftKey = false) => {
    if (!day.isCurrentMonth) return;
    
    // Check if the selected date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    const selectedDateOnly = new Date(day.date);
    selectedDateOnly.setHours(0, 0, 0, 0);
    
    if (selectedDateOnly < today) {
      // Past date - show no time slots and don't select
      setSelectedTimeSlots([]);
      setSelectedDate(day.date);
      return;
    }
    
    // For multi-selection with shift key
    if (shiftKey) {
      const dayKey = `${day.date.getFullYear()}-${day.date.getMonth()}-${day.date.getDate()}`;
      setSelectedDays(prev => {
        if (prev.includes(dayKey)) {
          return prev.filter(d => d !== dayKey);
        } else {
          return [...prev, dayKey];
        }
      });
    } else {
      // Single selection - clear all previous selections and select only this day
      setSelectedDate(day.date);
      
      // For normal click, clear all selections and select only this day
      const dayKey = `${day.date.getFullYear()}-${day.date.getMonth()}-${day.date.getDate()}`;
      setSelectedDays([dayKey]); // Replace entire array with just this day
      
      // For edit page, default to empty until user applies presets or custom
      setSelectedTimeSlots([]);
    }
  };

  const handleTimeSlotToggle = (slotId) => {
    setSelectedTimeSlots(prevSlots => 
      prevSlots.map(slot => {
        if (slot.id === slotId && slot.status === 'available') {
          return {
            ...slot,
            status: slot.status === 'selected' ? 'available' : 'selected'
          };
        }
        return slot;
      })
    );
  };

  const handlePresetTimeChange = (timeSlot) => {
    setPresetTimes(prev => ({
      ...prev,
      [timeSlot]: !prev[timeSlot]
    }));
  };

  const handleUnavailabilityChange = (reason) => {
    setUnavailabilityReasons(prev => {
      // Reset all checkboxes first
      const resetState = {
        sickLeave: false,
        annualLeave: false,
        other: false
      };
      // Set only the clicked checkbox to true
      resetState[reason] = !prev[reason];
      return resetState;
    });
  };



  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Top Banner */}
      <div className="bg-blue-600 text-white px-6 py-1 rounded-lg mb-6">
        <h1 className="text-base font-semibold text-center">Edit Availability</h1>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex gap-2">
          {/* Column 1: Navigation Items (17%) */}
          <div className="w-[17%]">
            <div className="flex flex-col space-y-2">
              <Link to="/clinician/availability/current" className="w-[80%] bg-white text-blue-600 py-2 px-3 rounded-lg font-medium text-sm border border-gray-300 hover:bg-gray-50 transition-all duration-200 text-center transform hover:scale-105 shadow-sm hover:shadow-md">
                Current Availability
              </Link>
              <button className="w-[80%] bg-blue-600 text-white py-2 px-3 rounded-lg font-medium text-sm text-center transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg">
                Edit Availability
              </button>
            </div>
          </div>

          {/* Column 2: Year Section (23%) */}
          <div className="w-[23%] -ml-4">
            <div className="bg-blue-600 text-white px-3 py-2 rounded-t-lg">
              <h3 className="font-semibold text-center text-sm">Year</h3>
            </div>
            
            {/* Notes Panel */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
              <p className="text-xs text-yellow-800">
                Select an individual month or hold shift to select multiple months. Public Holidays will be automatically removed from availability.
              </p>
            </div>
            
            <div className="border border-gray-300 rounded-b-lg p-4">
              <div className="flex items-center justify-center space-x-4 mb-4">
                <button 
                  onClick={() => handleYearChange('prev')}
                  className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-blue-50 transition-all duration-200 transform hover:scale-110"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="font-bold text-gray-800 text-lg px-4 py-2 bg-gray-50 rounded-lg min-w-[80px] text-center shadow-sm">{selectedYear}</span>
                <button 
                  onClick={() => handleYearChange('next')}
                  className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-blue-50 transition-all duration-200 transform hover:scale-110"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              
              {/* Months Grid */}
              <div className="grid grid-cols-2 gap-2 mt-4">
                {months.map((month, index) => (
                  <button
                    key={month}
                    onClick={(e) => handleMonthSelection(index, e.shiftKey)}
                    className={`py-1 px-2 text-sm font-medium rounded border transition-all duration-200 transform hover:scale-105 ${
                      selectedMonths.includes(index)
                        ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                        : 'bg-white text-blue-600 border-gray-300 hover:bg-blue-50 shadow-sm hover:shadow-md'
                    }`}
                  >
                    {month}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Column 3: Month Section (30%) */}
          <div className="w-[30%]">
            <div className="bg-blue-600 text-white px-3 py-2 rounded-t-lg">
              <h3 className="font-semibold text-center text-sm">Month</h3>
            </div>
            
            {/* Notes Panel */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
              <p className="text-xs text-yellow-800">
                Select an individual day or hold shift to select multiple days. Public Holidays will be automatically removed from availability.
              </p>
            </div>
            
            <div className="border border-gray-300 rounded-b-lg p-3">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <button 
                  onClick={() => handleMonthChange('prev')}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="font-medium text-gray-700 text-sm">{months[selectedMonth]}</span>
                <button 
                  onClick={() => handleMonthChange('next')}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                {/* Days of Week Header */}
                <div className="grid grid-cols-7 bg-blue-600">
                  {daysOfWeek.map(day => (
                    <div key={day} className="px-1 py-1 text-center">
                      <span className="text-xs font-medium text-white">{day}</span>
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7">
                  {calendarDays.map((day, index) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const dayDate = new Date(day.date);
                    dayDate.setHours(0, 0, 0, 0);
                    const isPastDate = dayDate < today;
                    const isAvailable = availableDates.includes(day.day) && day.isCurrentMonth && !isPastDate;
                    const dayKey = `${day.date.getFullYear()}-${day.date.getMonth()}-${day.day}`;
                    const isSelected = selectedDays.includes(dayKey);
                    
                    return (
                      <div
                        key={index}
                        className={`px-1 py-1 text-center border-r border-b border-gray-300 min-h-[30px] flex items-center justify-center cursor-pointer ${
                          !day.isCurrentMonth
                            ? 'bg-gray-50 text-gray-400'
                            : isPastDate
                              ? 'bg-gray-200 text-gray-500'
                              : isSelected
                                ? 'bg-blue-600 text-white'
                                : isAvailable
                                  ? 'bg-green-700 text-white'
                                  : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                        onClick={(e) => handleDaySelection(day, e.shiftKey)}
                      >
                        <span className="text-xs font-medium">{day.day}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Column 4: Time Section (30%) */}
          <div className="w-[30%] space-y-3">
            <div className="bg-blue-600 text-white px-3 py-2 rounded-t-lg">
              <h3 className="font-semibold text-center text-sm">Time</h3>
            </div>
            
            {/* Notes Panel */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
              <p className="text-xs text-yellow-800">
                Mandatory breaks will automatically be added to your calendar.
              </p>
            </div>
            
            <div className="border border-gray-300 rounded-b-lg p-3">
              {/* Preset Times Section */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-800 text-sm mb-2">Preset Times</h4>
                <p className="text-xs text-gray-600 mb-3">Preset times include 2 x 15min & 1 x 30min break.</p>
                
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {Object.keys(presetTimes).map(timeSlot => (
                    <label key={timeSlot} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={presetTimes[timeSlot]}
                        onChange={() => handlePresetTimeChange(timeSlot)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-all duration-200"
                      />
                      <span className="text-xs text-gray-700">{timeSlot}</span>
                    </label>
                  ))}
                </div>
                
                <label className="flex items-center space-x-2 mb-4">
                  <input
                    type="checkbox"
                    checked={applyToAllDays}
                    onChange={() => setApplyToAllDays(!applyToAllDays)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-700">Apply To All Days</span>
                </label>
                
                <div className="flex justify-end">
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Applying...' : 'Submit'}
                  </button>
                </div>
              </div>
              
              {/* Custom Times Section */}
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-800 text-sm mb-2">Custom Times</h4>
                
                <div className="mb-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Start Time: Select Time
                      </label>
                      <input
                        type="time"
                        value={customStartTime}
                        onChange={(e) => setCustomStartTime(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs transition-all duration-200"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Finish Time: Select Time
                      </label>
                      <input
                        type="time"
                        value={customFinishTime}
                        onChange={(e) => setCustomFinishTime(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs transition-all duration-200"
                      />
                    </div>
                  </div>
                </div>
                
                <label className="flex items-center space-x-2 mb-4">
                  <input
                    type="checkbox"
                    checked={applyCustomToAllDays}
                    onChange={() => setApplyCustomToAllDays(!applyCustomToAllDays)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-all duration-200"
                  />
                  <span className="text-xs text-gray-700">Apply To All Days</span>
                </label>
                
                <div className="flex justify-end">
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-medium hover:bg-blue-700 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? 'Applying...' : 'Submit'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicianEditAvailabilityPage;